/** @file Animation-frame clock: calls tick(dt seconds) once per frame until stopped; dt is capped. */

/** The browser hooks the clock uses (injected so tests drive it without a DOM). */
export interface ClockDeps {
  /** Milliseconds (performance.now in the editor). */
  now(): number;
  requestFrame(callback: () => void): number;
  cancelFrame(handle: number): void;
}

/** Longest step in seconds: a background tab or a debugger pause never jumps playback further. */
const MAX_DT = 0.1;

/** Starts the clock; the first tick comes one frame after the start. Returns the idempotent stop. */
export function createPlaybackClock(deps: ClockDeps, tick: (dt: number) => void): () => void {
  let last = deps.now();
  let stopped = false;
  let handle = 0;
  // Read through a call: `tick` may stop the clock, which a narrowed `stopped` would hide from the type checker.
  const running = (): boolean => !stopped;
  const frame = (): void => {
    if (!running()) return;
    const now = deps.now();
    const dt = Math.min(MAX_DT, Math.max(0, (now - last) / 1000));
    last = now;
    tick(dt);
    if (running()) handle = deps.requestFrame(frame);
  };
  handle = deps.requestFrame(frame);
  return () => {
    if (stopped) return;
    stopped = true;
    deps.cancelFrame(handle);
  };
}
